import React, { useState, useEffect, useRef } from 'react';
import { withStyles, makeStyles } from '@material-ui/core/styles';

import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import Fade from "@material-ui/core/Fade";
import Paper from "@material-ui/core/Paper";
import Slider from '@material-ui/core/Slider';
import Popover from '@material-ui/core/Popover';
import { IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import { TextField, RadioGroupField } from '../fields';
import ButtonLink from 'components/buttons/ButtonLink';
import Modal from 'components/modals/Modal';

import { useFields } from "hooks/fields";
import { fetchAuthenticated, get } from 'services/fetch';
import { emitEvent } from 'utils/events';
import { getLockStateLabel, getLockStateColor } from 'utils/helpers';

import { Stage, Layer, Rect, Text, Image, Circle, Line } from "react-konva";
import ReactSVG from 'react-svg'
import useImage from 'use-image';

function formatLockDatetimeBeacon(datetimeUtcStr) {
  if (!datetimeUtcStr) return null;
  try {
    const y = datetimeUtcStr.substring(0, 4);
    const mo = datetimeUtcStr.substring(4, 6);
    const d = datetimeUtcStr.substring(6, 8);
    const h = datetimeUtcStr.substring(8, 10);
    const mi = datetimeUtcStr.substring(10, 12);
    const s = datetimeUtcStr.substring(12, 14);
    const utcDate = new Date(`${y}-${mo}-${d}T${h}:${mi}:${s}Z`);
    return utcDate.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  } catch (e) {
    return datetimeUtcStr;
  }
}

function hexToRgba(hex, alpha) {
  if (!hex || typeof hex !== 'string' || hex[0] !== '#') return null;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  if ([r, g, b].some(Number.isNaN)) return null;
  return `rgba(${r},${g},${b},${alpha})`;
}

function getEquipDateInfo(equip) {
  var lastComm = 'Indisponível';
  var lastPos = 'Indisponível';
  var ev = equip.lastEventEquipment || equip.lastEvent;
  if (ev) {
    if (ev.timestamp) {
      try {
        lastComm = new Date(ev.timestamp).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
      } catch (e) { /* keep default */ }
    }
    if (ev.lastValidPositionDate) {
      lastPos = ev.lastValidPositionDate + (ev.lastValidPositionHour ? ' ' + ev.lastValidPositionHour : '');
    } else {
      lastPos = lastComm;
    }
  }
  return { lastComm: lastComm, lastPos: lastPos };
}

const useStyles = makeStyles(theme => ({
  listItemLabel: {
    fontSize: 16,
    color: "#41475a",
    fontWeight: "bold"
  },
  fullWidthField: {
    maxWidth: '85%',
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  truckWrapper: {
    position: 'relative',
    display: 'inline-block',
  },
  lockStatusRow: {
    position: 'absolute',
    top: -40,
    left: '60%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: 8,
    zIndex: 2,
    pointerEvents: 'none',
  },
  lockStatusPill: {
    padding: '7px 16px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '0.02em',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    whiteSpace: 'nowrap',
    border: '1px solid rgba(0,0,0,0.06)',
  },
  lockBadge: {
    position: 'absolute',
    top: 95,
    right: -14,
    width: 26,
    height: 26,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #43a047 0%, #2e7d32 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(46,125,50,0.45)',
    cursor: 'pointer',
    zIndex: 2,
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      transform: 'scale(1.15)',
      boxShadow: '0 4px 16px rgba(46,125,50,0.55)',
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      width: 26,
      height: 26,
      borderRadius: '50%',
      border: '2px solid rgba(67,160,71,0.4)',
      animation: '$lockPulse 2s ease-in-out infinite',
    },
  },
  '@keyframes lockPulse': {
    '0%': { transform: 'scale(1)', opacity: 1 },
    '50%': { transform: 'scale(1.6)', opacity: 0 },
    '100%': { transform: 'scale(1)', opacity: 0 },
  },
  '@keyframes lockPulseEmergency': {
    '0%':   { transform: 'scale(1)',   opacity: 1 },
    '50%':  { transform: 'scale(1.8)', opacity: 0 },
    '100%': { transform: 'scale(1)',   opacity: 0 },
  },
  lockBadgeEmergency: {
    background: 'linear-gradient(135deg, #e53935 0%, #b71c1c 100%)',
    boxShadow: '0 2px 8px rgba(183,28,28,0.55)',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(183,28,28,0.65)',
    },
    '&::after': {
      border: '2px solid rgba(229,57,53,0.5)',
      animation: '$lockPulseEmergency 1s ease-in-out infinite',
    },
  },
  lockEmergencyAlert: {
    marginTop: 10,
    paddingTop: 8,
    borderTop: '1px solid rgba(229,57,53,0.3)',
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  lockEmergencyLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: '#ef9a9a',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  lockEmergencyValue: {
    fontSize: 12,
    color: '#ffcdd2',
  },
  lockBadgeIcon: {
    fontSize: 14,
    color: '#fff',
    lineHeight: 1,
  },
  lockTooltip: {
    position: 'absolute',
    top: 60,
    right: 46,
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
    borderRadius: 10,
    padding: '14px 16px',
    minWidth: 210,
    boxShadow: '0 8px 32px rgba(0,0,0,0.28), 0 2px 8px rgba(0,0,0,0.12)',
    zIndex: 3,
    opacity: 0,
    transform: 'translateX(8px)',
    transition: 'opacity 0.25s ease, transform 0.25s ease',
    pointerEvents: 'none',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 48,
      right: -6,
      width: 12,
      height: 12,
      background: '#334155',
      transform: 'rotate(45deg)',
      borderRadius: 2,
    },
  },
  lockTooltipVisible: {
    opacity: 1,
    transform: 'translateX(0)',
    pointerEvents: 'auto',
  },
  lockTooltipHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 10,
    paddingBottom: 8,
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  lockTooltipIcon: {
    width: 20,
    height: 20,
    borderRadius: 4,
    background: 'linear-gradient(135deg, #43a047 0%, #2e7d32 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 8,
    fontSize: 11,
    flexShrink: 0,
  },
  lockTooltipName: {
    fontSize: 13,
    fontWeight: 700,
    color: '#f1f5f9',
    letterSpacing: '0.3px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  lockTooltipRow: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 6,
    '&:last-child': {
      marginBottom: 0,
    },
  },
  lockTooltipLabel: {
    fontSize: 9,
    fontWeight: 700,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    marginBottom: 1,
  },
  lockTooltipValue: {
    fontSize: 12,
    fontWeight: 500,
    color: '#e2e8f0',
  },
  equipTooltip: {
    position: 'absolute',
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
    borderRadius: 10,
    padding: '12px 14px',
    minWidth: 195,
    boxShadow: '0 8px 32px rgba(0,0,0,0.28), 0 2px 8px rgba(0,0,0,0.12)',
    zIndex: 10,
    opacity: 0,
    transform: 'translateY(4px)',
    transition: 'opacity 0.2s ease, transform 0.2s ease',
    pointerEvents: 'none',
  },
  equipTooltipVisible: {
    opacity: 1,
    transform: 'translateY(0)',
  },
  equipTooltipHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 8,
    paddingBottom: 7,
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  equipTooltipDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    marginRight: 8,
    flexShrink: 0,
  },
  equipTooltipName: {
    fontSize: 12,
    fontWeight: 700,
    color: '#f1f5f9',
    letterSpacing: '0.3px',
  },
  equipTooltipType: {
    fontSize: 10,
    color: '#94a3b8',
    marginLeft: 'auto',
    paddingLeft: 8,
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    // padding: theme.spacing(4),
    outline: 'none',
    display: 'flex',
    borderRadius: 8,
  },
  popoverContainer: {
    width: 248,
  },
  popoverHeader: props => ({
    width: '100%',
    backgroundColor: props.color,
    height: 68,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  }),
  popoverContent: {
    paddingLeft: 18,
    paddingRight: 18,
    paddingTop: 28,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  popoverIcon: {
  },
  popoverBlueLabel: {
    fontWeight: 'bold',
    fontSize: 14,
    color: theme.palette.primary.main,
    marginBottom: 10,
  },
  popoverLabel: {
    fontWeight: 'bold',
    fontSize: 14,
    color: theme.palette.gray.gray_5,
  },
  popoverValue: {
    fontSize: 16,
    color: theme.palette.gray.gray_4,
    fontWeight: '600',
  },
  popoverAction: {
    borderTop: `solid 1px rgba(128, 132, 146, 0.3)`,
    width: '100%',
    marginTop: 20,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  popoverCloseIcon: {
    position: 'absolute',
    right: 0,
    top: 0,
    color: 'white',
  },
}));

const MySlider = withStyles({
  root: {
    paddingBottom: 20,
  },
  thumb: {
    boxShadow: '0 2px 7px 0 #0000004e',
    height: 15,
    width: 15,
    backgroundColor: '#fff',
    // marginTop: -8,
    // marginLeft: -12,
    // '&:focus,&:hover,&$active': {
      // boxShadow: 'inherit',
    // },
  },
  active: {},
  valueLabel: {
    left: 'calc(-50%)',
    // top: -22,
    marginTop: 57,
    color: '#41475a',
    fontSize: 16,
    fontWeight: '600',
    '& *': {
      background: 'transparent',
      color: '#000',
    },
  },
  track: {
    height: 6,
    borderRadius: 3,
    color: '#548cc1',
  },
  rail: {
    height: 6,
    borderRadius: 3,
    color: '#d9dade',
  },
})(Slider);

const circleRadius = 7;

const Icons = {
  'Cavalo': require('assets/icon/cavalo.svg'),
  'Carreta': require('assets/icon/carreta.svg'),
  'Isca': require('assets/icon/pin.svg'),
}

const circles = [
  {_id: 1, x: 69, y: 168,  radius: circleRadius, fill: '#b2b7c7'},
  {_id: 2, x: 305, y: 210, radius: circleRadius, fill: '#b2b7c7'},
  {_id: 3, x: 310, y: 110, radius: circleRadius, fill: '#b2b7c7'},
  {_id: 4, x: 350, y: 110, radius: circleRadius, fill: '#b2b7c7'},
  {_id: 5, x: 390, y: 110, radius: circleRadius, fill: '#b2b7c7'},
];

const getHeaderColor = (equip1, equip2) => {
  if(!equip1 || !equip2) {
    return '#41475a';
  }
  return equip1._id === equip2._id && '#548cc1' || '#41475a';
}


const BeaconTruck = (props) => {
  const { equipment, possibleEquipments, setIsLoading } = props;
  const [beacons, setBeacons] = useState([]);
  const [image] = useImage(require('assets/img/truck.png'));
  const [lockTooltipOpen, setLockTooltipOpen] = useState(false);
  const [hoveredEquip, setHoveredEquip] = useState(null);
  const [hoveredPos, setHoveredPos] = useState({ x: 0, y: 0 });

  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [popoverPosition, setIsPopoverPosition] = useState({x: 0, y: 0});
  const [currentPopoverEquipment, setCurrentPopoverEquipment] = useState();
  const [currentPopoverBeacon, setCurrentPopoverBeacon] = useState();
  const [showPopoverSlider, setShowPopoverSlider] = useState(false);
  const [popoverDistance, setPopoverDistance] = useState(50);

  /* used to keep position in screen information */
  const [possibleEquipmentsWithPosition, setPossibleEquipmentsWithPosition] = useState([]);
  const [beaconsWithPosition, setBeaconsWithPosition] = useState([]);
  const [equipmentWithPosition, setEquipmentWithPosition ] = useState({
    position: {x: -10, y: -10}
  });

  const classes = useStyles({
    color: getHeaderColor(currentPopoverEquipment, equipment),
  });

  const slotsCounter = useRef(0);

  const positions = {
    'Cavalo': () =>  { return { x: 69, y: 168 }},
    'Carreta': () => { return { x: 305, y: 210 }},
    'Isca': (counterRef) => {
      return {
        x: 310 + (counterRef.current++ * 40), y: 110
      }
    },
  }

  /* calculate position in screen */
  useEffect(() => {
    slotsCounter.current = 0;

    try {
      const position = positions[equipment.equipmentType.name](slotsCounter);
      setEquipmentWithPosition({
        ...equipment, position,
      });

      const _beacons = [];
      possibleEquipments.forEach((equip) => {
        _beacons.push({
          ...equip,
          position: positions[equip.equipmentType.name](slotsCounter),
        })
      });
      setPossibleEquipmentsWithPosition(_beacons);
    } catch (e) {
      console.log('Equipment type is not one of ["Cavalo", "Carreta","Isca"]');
      console.log(e);
    }
  }, [possibleEquipments]);

  // Load beacons
  useEffect(() => {
    if (equipment) {
      loadBeacons();
    }
  }, [equipment]);

  useEffect(() => {
    setBeaconsWithPosition(beacons.map(beacon => {
      const destinyPosition = possibleEquipmentsWithPosition.filter(e => e._id === beacon.beacon)[0];
      return {
        ...beacon,
        position: destinyPosition.position,
      }
    }));
  }, [beacons]);

  const loadBeacons = async () => {
    setIsLoading(true);
    try {
      (async () => {
        const response = await fetchAuthenticated(
          'get',
          `beacons/${equipment._id}`
        );
        if (!response.ok) {
          setBeacons([]);
        } else {
          const data = await response.json();
          setBeacons(data);
        }
      })();
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  }

  const SlotsPlaceholder = (props) => {
    return (
      <>
        {circles.map((circle) => (
          <Circle
            onMouseEnter={() => {}}
            onMouseLeave={() => {}}
            onClick={() => {}}
            key={circle._id}
            {...circle}
          ></Circle>
        ))}
      </>
    )
  }

  const onAnchor = async (equip) => {
    setIsLoading(true);
    // console.log(popoverDistance);
    const beacon = {
      type: 'system',
      beacon: equip.imei,
      distance: popoverDistance,
      equipment: equipment.imei,
    }
    const response = await fetchAuthenticated('post', 'beacons', beacon);
    if(response.ok) {
      emitEvent('showSnack', { message: 'Equipamento ancorado.', type: 'success' });
    }
    setIsPopoverOpen(false);
    loadBeacons();
  }

  const handleRemoveBeacon = async (beaconToRemove) => {
    try {
      setIsLoading(true);
      const response = await fetchAuthenticated(
        `delete`,
        `beacons/${beaconToRemove._id}`
      );
      if (response.ok) {
        emitEvent('showSnack', { message: 'Beacon removido.', type: 'success' });
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsPopoverOpen(false);
      loadBeacons();
    }
  };

  /* reset everything when popup is closed */
  useEffect(() => {
    if(!isPopoverOpen) {
      setShowPopoverSlider(false);
      setCurrentPopoverBeacon();
      // setCurrentPopoverEquipment();
      setShowPopoverSlider(false);
    }
  }, [isPopoverOpen]);

  const onPopoverClose = () => {
    setIsPopoverOpen(false);
  }

  const onCircleClick = (e, equip) => {
    setIsPopoverPosition({
      x: e.evt.clientX,
      y: e.evt.clientY,
    })
    setIsPopoverOpen(true);
    setCurrentPopoverEquipment(equip);
    const currentBeacon = beacons.filter(b => b.beacon === equip._id);
    if(currentBeacon.length) {
      setCurrentPopoverBeacon(currentBeacon[0]);
    }
  }

  const renderPopooverContent = (props) => {
    if(currentPopoverEquipment && currentPopoverEquipment._id === equipment._id) {
      return (
        <>
          <Typography className={classes.popoverBlueLabel}>Este equipamento</Typography>
          <Typography className={classes.popoverLabel}>IMEI</Typography>
          <Typography className={classes.popoverValue} style={{marginBottom: 18}}>
            {currentPopoverEquipment.shortImei}
          </Typography>
        </>
      )
    }

    if(currentPopoverBeacon) {
      return (
        <>
          <Typography className={classes.popoverLabel}>IMEI</Typography>
          <Typography className={classes.popoverValue}>{currentPopoverEquipment.shortImei}</Typography>
          <Typography className={classes.popoverLabel} style={{paddingTop: 16}}>DISTÂNCIA</Typography>
          <Typography className={classes.popoverValue}>{currentPopoverBeacon.distance}</Typography>
          <div className={classes.popoverAction}>
            <ButtonLink
              onClick={() => {
                handleRemoveBeacon(currentPopoverBeacon);
              }}
              title="Desancorar"
            />
          </div>
        </>
      )
    }

    if(!showPopoverSlider) {
      return (
        <>
          <Typography className={classes.popoverLabel}>IMEI</Typography>
          <Typography className={classes.popoverValue}>{currentPopoverEquipment.shortImei}</Typography>
          <div className={classes.popoverAction}>
            <ButtonLink
              onClick={() => {
                setShowPopoverSlider(true);
              }}
              title="Ancorar"
            />
          </div>
        </>

      )
    }

    if(showPopoverSlider) {
      return (
        <>
          <Typography className={classes.popoverLabel}>RAIO MÁXIMO (METROS)</Typography>
          <MySlider
            defaultValue={popoverDistance}
            onChange={(e, value) => {
              setPopoverDistance(value);
            }}
            // getAriaValueText={valuetext}
            valueLabelDisplay="on"
            min={0}
            max={10000}
          />
          <div className={classes.popoverAction}>
            <ButtonLink
              onClick={() => {
                onAnchor(currentPopoverEquipment);
              }}
              title="Salvar"
            />
          </div>
        </>
      )
    }
  }

  const lockInfo = equipment && equipment.lock;
  const lockEvent = equipment && (equipment.lastEventLock || equipment.lastEvent);
  const lockData = lockEvent && lockEvent.lockData;
  const lockName = (lockInfo && lockInfo.name) || (lockData && lockData.unitnumber) || null;
  const lockLastComm = lockData && formatLockDatetimeBeacon(lockData.datetime_actual);
  const lockLastPos = lockData && formatLockDatetimeBeacon(lockData.datetime_utc);
  const lockLogicState = lockData && lockData.logic_state;
  const lockStateColor = getLockStateColor(lockLogicState);
  const lockStateLabel = getLockStateLabel(lockLogicState);
  const hasLock = !!(lockInfo && lockInfo._id);
  const isEmergency = hasLock && equipment && equipment.currentHandlingStatus === 'emergency';
  const pillBg = isEmergency
    ? 'rgba(229, 57, 53, 0.16)'
    : hexToRgba(lockStateColor || '#43a047', 0.16) || 'rgba(67, 160, 71, 0.16)';
  const pillFg = isEmergency ? '#b71c1c' : (lockStateColor || '#1b5e20');

  const rawLockGpsValid = lockData && lockData.gps_valid;
  const lockGpsValid = (rawLockGpsValid === undefined || rawLockGpsValid === null || rawLockGpsValid === '')
    ? undefined
    : String(rawLockGpsValid);
  const gpsLabel = lockGpsValid === '1' ? 'GPS' : lockGpsValid === '0' ? 'LBS' : 'Indisponível';
  const gpsPillBg = lockGpsValid === '1' ? 'rgba(52,152,219,0.14)' : lockGpsValid === '0' ? 'rgba(243,156,18,0.14)' : 'rgba(160,160,160,0.12)';
  const gpsPillFg = lockGpsValid === '1' ? '#1a5276' : lockGpsValid === '0' ? '#7d6608' : '#555';

  return (
     <div className={classes.center} >
      <div className={classes.truckWrapper}>
        <Stage width={558} height={281}>
          <Layer>
            <Image image={image} ></Image>
              {beaconsWithPosition.map((beacon) => (
                <Line
                  key={beacon._id}
                  stroke="#548cc1"
                  points={[
                    beacon.position.x, beacon.position.y,
                    equipmentWithPosition.position.x, equipmentWithPosition.position.y,
                  ]}
                />
              ))}
            <SlotsPlaceholder/>
            <Circle
              onMouseEnter={() => {
                document.body.style.cursor = "pointer";
                setHoveredEquip(equipment);
                setHoveredPos(equipmentWithPosition.position);
              }}
              onMouseLeave={() => {
                document.body.style.cursor = "default";
                setHoveredEquip(null);
              }}
              onClick={(e) => {
                onCircleClick(e, equipment);
              }}
              x={equipmentWithPosition.position.x}
              y={equipmentWithPosition.position.y}
              radius={circleRadius}
              fill="#548cc1"
            />

            {possibleEquipmentsWithPosition.map(((equip, index) => (
              <Circle
                key={equip._id + index}
                onMouseEnter={() => {
                  document.body.style.cursor = "pointer";
                  setHoveredEquip(equip);
                  setHoveredPos(equip.position);
                }}
                onMouseLeave={() => {
                  document.body.style.cursor = "default";
                  setHoveredEquip(null);
                }}
                onClick={(e) => {
                  onCircleClick(e, equip);
                }}
                x={equip.position.x}
                y={equip.position.y}
                radius={circleRadius}
                fill="#41475a"
              />
            )))}

          </Layer>
        </Stage>

        {hasLock && (
          <>
            <div className={classes.lockStatusRow}>
              <div
                className={classes.lockStatusPill}
                style={{ backgroundColor: pillBg, color: pillFg }}
              >
                Trava: {lockStateLabel}
              </div>
            </div>
            <div
              className={`${classes.lockBadge} ${isEmergency ? classes.lockBadgeEmergency : ''}`}
              onMouseEnter={() => setLockTooltipOpen(true)}
              onMouseLeave={() => setLockTooltipOpen(false)}
              onClick={() => setLockTooltipOpen(!lockTooltipOpen)}
            >
              <span className={classes.lockBadgeIcon}>&#128274;</span>
            </div>

            <div className={`${classes.lockTooltip} ${lockTooltipOpen ? classes.lockTooltipVisible : ''}`}>
              <div className={classes.lockTooltipHeader}>
                <div className={classes.lockTooltipIcon}>
                  <span style={{ color: '#fff', fontSize: 10 }}>&#128274;</span>
                </div>
                <span className={classes.lockTooltipName}>{lockName || 'Cadeado'}</span>
              </div>
              <div className={classes.lockTooltipRow}>
                <span className={classes.lockTooltipLabel}>Status da trava</span>
                <span
                  className={classes.lockTooltipValue}
                  style={lockStateColor ? { color: lockStateColor, fontWeight: 600 } : undefined}
                >
                  {lockStateLabel}
                </span>
              </div>
              <div className={classes.lockTooltipRow}>
                <span className={classes.lockTooltipLabel}>Tipo da localização</span>
                <span className={classes.lockTooltipValue}>{gpsLabel}</span>
              </div>
              <div className={classes.lockTooltipRow}>
                <span className={classes.lockTooltipLabel}>Última posição válida</span>
                <span className={classes.lockTooltipValue}>{lockLastPos || 'Indisponível'}</span>
              </div>
              {isEmergency && (
                <div className={classes.lockEmergencyAlert}>
                  <span className={classes.lockEmergencyLabel}>⚠ Emergência ativa</span>
                  <span className={classes.lockEmergencyValue}>
                    Posições do equipamento sendo consideradas
                  </span>
                </div>
              )}
            </div>
          </>
        )}

        {hoveredEquip && (() => {
          const info = getEquipDateInfo(hoveredEquip);
          const isCurrentEquip = hoveredEquip._id === equipment._id;
          const dotColor = isCurrentEquip ? '#548cc1' : '#41475a';
          const typeName = hoveredEquip.equipmentType && hoveredEquip.equipmentType.name;
          return (
            <div
              className={`${classes.equipTooltip} ${classes.equipTooltipVisible}`}
              style={{ top: hoveredPos.y - 90, left: hoveredPos.x + 16 }}
            >
              <div className={classes.equipTooltipHeader}>
                <div className={classes.equipTooltipDot} style={{ background: dotColor }} />
                <span className={classes.equipTooltipName}>{hoveredEquip.shortImei}</span>
                {typeName && <span className={classes.equipTooltipType}>{typeName}</span>}
              </div>
              <div className={classes.lockTooltipRow}>
                <span className={classes.lockTooltipLabel}>Última posição válida</span>
                <span className={classes.lockTooltipValue}>{info.lastPos}</span>
              </div>
            </div>
          );
        })()}
      </div>

      <Popover
        open={isPopoverOpen}
        anchorReference="anchorPosition"
        anchorPosition={{ top: popoverPosition.y, left: popoverPosition.x }}
        classes={{
          paper: classes.paper,
        }}
        onClose={onPopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {/* <Paper > */}
        {currentPopoverEquipment &&
          <div className={classes.popoverContainer}>
            <div className={classes.popoverHeader}>
              <ReactSVG
                className={classes.popoverIcon}
                src={Icons[currentPopoverEquipment.equipmentType.name]}
              />

              <IconButton className={classes.popoverCloseIcon} onClick={onPopoverClose}>
                <CloseIcon />
              </IconButton>
              {/* <img className={classes.popoverIcon} ></img> */}
            </div>
            <div className={classes.popoverContent}>
              {renderPopooverContent()}
            </div>
          </div>
        }
        {/* </Paper> */}
      </Popover>
    </div>
  );
}
BeaconTruck.defaultProps = {

};

function ModalBeacon({ setIsAnchored, equipment, isVisible, ...rest }) {
  const [isLoading, setIsLoading] = useState(true);
  const [possibleEquipments, setPossibleEquipments] = useState([]);


   // Load possible beacons
  useEffect(() => {
    if (equipment && equipment.vehicle) {
      (async () => {
        const response = await get(
          'equipment?notPaginated=true&fields=_id,imei,shortImei,equipmentType,lastEvent,lastEventEquipment,lastEventLock,lock',
          {
            'currentCompany._id': {value: equipment.currentCompany._id, toObjectId: false},
            $expr: { $ne: ["$_id", equipment._id]},
            'equipmentType.name': { $in: ['Cavalo', 'Carreta', 'Isca']},
            vehicle: {$exists: true, $eq: equipment.vehicle},
            // vehicle: equipment.vehicle,
            // vehicle: {$exists: true}
            // vehicle: {
            //   $ne: undefined,
            //   $ne: null,
            //   $ne: '',
            //   $eq: equipment.vehicle ,
            // },
          },
          '&',
        );
        setPossibleEquipments(response.data);
      })();
    }
  }, [equipment]);

  useEffect(() => {
    const loadBeacons = async () => {
      try {
        (async () => {
          if(equipment) {
            const response = await fetchAuthenticated(
              'get',
              `beacons/${equipment._id}`
            );
            const data = await response.json();
            if(data.length > 0) {
              setIsAnchored(true);
            }
          }
       })();
      } catch (err) {
        console.log(err);
      }
    }
    loadBeacons();
  }, [equipment]);


  return (
    <Modal
      title="Status de comunicação"
      hideButton
      isLoading={isLoading}
      isVisible={isVisible}
      ModalHeaderProps={{
        style: {
          marginBottom: 35
        }
      }}
      ModalButtonContainerProps={{
        style: {
          marginTop: 35
        }
      }}
      {...rest}
    >
      <BeaconTruck
        equipment={equipment}
        possibleEquipments={possibleEquipments}
        setIsLoading={setIsLoading}
      />
    </Modal>
  );
}

ModalBeacon.defaultProps = {
};

export default ModalBeacon;
